import { type KeyboardEvent, type SyntheticEvent, useCallback, useMemo } from 'react';

export interface UseKeyboardActivationParams {
  hasClickEvent: boolean;
  onClick?: (event: SyntheticEvent) => void;
}

export type KeyboardActivationProps =
  | { tabIndex: 0; onKeyDown: (event: KeyboardEvent) => void }
  | { tabIndex?: undefined; onKeyDown?: undefined };

/**
 * Makes a non-button root (div/Box/Stack) keyboard-activate its own `onClick`
 * on Enter/Space. Fires only when the root element itself is focused
 * (`target === currentTarget`): a keydown bubbling up from a focusable
 * descendant (input, nested button/link) must keep its native behaviour, not
 * trigger the block — this is what the mouse path already guarantees via
 * `markEventAsHandledByBlock`. Auto-repeat is ignored so a held key does not
 * spam the interaction.
 */
export function useKeyboardActivation({
  hasClickEvent,
  onClick,
}: UseKeyboardActivationParams): KeyboardActivationProps {
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.repeat) return;
      event.preventDefault();
      onClick?.(event);
    },
    [onClick],
  );

  return useMemo<KeyboardActivationProps>(
    () =>
      hasClickEvent && onClick
        ? { tabIndex: 0, onKeyDown }
        : { tabIndex: undefined, onKeyDown: undefined },
    [hasClickEvent, onClick, onKeyDown],
  );
}
